import { useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daih74jp14a1h3mr747g";

export default function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-100 lg:grid-cols-2">
          <div className="flex flex-col justify-center gap-4 bg-primary-950 p-8 text-background-100 md:p-10">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-background-50/20 bg-background-50/10 px-3 py-1 text-xs font-medium text-accent-400">
              <i className="ri-notification-3-line text-sm"></i>
              更新订阅
            </span>
            <h2 className="font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
              新合集一上线，第一时间通知你
            </h2>
            <p className="text-sm leading-relaxed text-background-200">
              留下接收邮箱，并选择你最常看的方向。我们会按你的关注范围推送最新收录的黑料照片合集、原图与打包整档，不做无关打扰。
            </p>
            <ul className="mt-2 flex flex-col gap-2 text-sm text-background-200">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-base text-accent-400"></i>
                新事件补档提醒
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-base text-accent-400"></i>
                打包整包上线通知
              </li>
            </ul>
          </div>

          <div className="p-8 md:p-10">
            <form id="subscribe-update-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="subscribe-email" className="text-sm font-semibold text-foreground-800">
                  接收邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="subscribe-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="subscribe-focus" className="text-sm font-semibold text-foreground-800">
                  关注方向
                </label>
                <select
                  id="subscribe-focus"
                  name="focus"
                  defaultValue="全部收录"
                  className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="全部收录">全部收录</option>
                  <option value="人物合集">人物合集</option>
                  <option value="事件归档">事件归档</option>
                  <option value="打包专区">打包专区</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="subscribe-note" className="text-sm font-semibold text-foreground-800">
                  想优先看到的内容（选填）
                </label>
                <textarea
                  id="subscribe-note"
                  name="remark"
                  rows={3}
                  maxLength={500}
                  placeholder="例如：某位人物或某起事件的补档进度"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
                readOnly
                className="field-check-verify"
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-line text-base"}></i>
                {status === "submitting" ? "提交中…" : "订阅更新提醒"}
              </button>

              {status === "success" ? (
                <p className="flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line text-base text-secondary-500"></i>
                  订阅成功，最新收录上线后会按你的关注方向通知你。
                </p>
              ) : null}

              {status === "error" ? (
                <p className="flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line text-base text-primary-600"></i>
                  {formError}
                </p>
              ) : null}

              <p className="text-xs leading-relaxed text-foreground-500">
                提交即表示你同意接收更新提醒，本站仅用于信息归档展示，不会将邮箱用于其他用途。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}